import { ContributionAmountResponseDto } from 'enrollment/queries/useContributionAmountQuery';
import { HSAContributionsResponseDto, ContributionAdjustmentDetails, ScheduledContributionDto } from 'enrollment/queries/useV3ContributionAmountQuery/utils/types';
import { ANNUAL_METHOD, MONTHLY_METHOD, CONTRIBUTION_FREQUENCY_TYPE_PER_PAY_PERIOD, FIXED, DONT_CONTRIBUTE } from './constants';
import { convertCurrency } from "../../../utils";
import { CompanySettingsResponseDto, NEW_HIRE_ENROLLMENT_TYPE } from "../../../../theSource/queries";
import { BenefitDto } from "../../../queries/useBenefitPlansQuery/types";

export const valueWithinBounds = (value: number, min: number, max: number) => {
  let returnVal = value;
  if (value < min) {
    returnVal = min;
  } else if (value > max) {
    returnVal = max;
  }

  return returnVal;
};

export const getEEContributionDisplay = (
  value: number,
  contributionData: ContributionAmountResponseDto,
  enrollmentMethod: string
) => {
  switch (enrollmentMethod) {
    case ANNUAL_METHOD:
      return value * contributionData.data.remaining_paydays;
    case MONTHLY_METHOD:
      return (
        value *
        (contributionData.data.remaining_paydays /
          contributionData.data.remaining_months_until_contribution_end)
      );
    default:
      return value;
  }
};

export const isHSANewExperienceEnabled = (companySettings: CompanySettingsResponseDto | undefined, benefit: BenefitDto, enrollmentContextType: string) => {
  return companySettings?.data?.hsa_new_experience_feature_toggle &&
    benefit?.benefit_lookup_code === 'hsa' &&
    enrollmentContextType === NEW_HIRE_ENROLLMENT_TYPE;
};

export const contributionDetails = (contributionType?: string, employeeContributionAmount?: string | number, remainingPaydays?:number | undefined | any) => {
  let contributionObj = {
    maxContributionAmount : 0,
    contributionCalculationType: '',
    contributionLabels: '',
    perPayPeriodAmountLabel: '',
    employeeAnnualContributionAmount: 0,
    employeePPPContributionAmount: 0,
  };
  if (contributionType === CONTRIBUTION_FREQUENCY_TYPE_PER_PAY_PERIOD) {
    contributionObj = {
      maxContributionAmount : Number(employeeContributionAmount) / remainingPaydays,
      contributionCalculationType: "*",
      contributionLabels: 'employeeContribution.perPayContribution',
      perPayPeriodAmountLabel: 'employeeContribution.totalPayPeriodAmount',
      employeePPPContributionAmount: employeeContributionAmount ? Number(employeeContributionAmount) * remainingPaydays : 0,
      employeeAnnualContributionAmount: employeeContributionAmount ? Number(employeeContributionAmount) * remainingPaydays : 0
    };
  } else {
    contributionObj = {
      maxContributionAmount : Number(employeeContributionAmount),
      contributionCalculationType: "/",
      contributionLabels: 'employeeContribution.annualPayPeriodContribution',
      perPayPeriodAmountLabel: 'employeeContribution.perPayPeriodAmount',
      employeePPPContributionAmount: employeeContributionAmount ? Number(employeeContributionAmount) / remainingPaydays : 0,
      employeeAnnualContributionAmount: employeeContributionAmount ? Number(employeeContributionAmount) : 0
    };
  }
  return contributionObj;
};

export const renderTotalContributionEmployeePanel = (hsaContribution: any, employeeMaxAmountContribution: number | string) => {
  const total = hsaContribution?.scheduled_contribution?.reduce((amount: number, contribution: any) => {
    return amount +
      (contribution?.scheduled_subscriber_contribution ? parseFloat(contribution.scheduled_subscriber_contribution) : 0) +
      (contribution?.scheduled_subscriber_lump_sum_contribution ? parseFloat(contribution.scheduled_subscriber_lump_sum_contribution) : 0);
    }, 0);
  const verifyingContributionExceeded = total <= Number(employeeMaxAmountContribution) ? total : Number(employeeMaxAmountContribution);
  const formattedTotal = total > 0 ? convertCurrency(String(verifyingContributionExceeded.toFixed(2))) + ' / ' : '';
  const formattedEmployeeMax = convertCurrency(String(employeeMaxAmountContribution));
  return (formattedTotal + formattedEmployeeMax).trim();
};

export const regularEmployeeContributionAmount = (hsaContribution: any, key: string) => {
  return Number(hsaContribution?.scheduled_contribution?.reduce((total: number, contribution: any) => {
    return total + (contribution[key] ? parseFloat(contribution[key]) : 0);
  }, 0) || 0).toFixed(2);
};

export const validateLumpSumTotal = (lumpSumEntries: { id: number, amount: string, date: string }[], remainingLumpSumAmount: number) => {
  const totalLumpSumAmount = lumpSumEntries.reduce((total, entry) => total + (parseFloat(entry.amount) || 0), 0);
  return totalLumpSumAmount > remainingLumpSumAmount;
};

export const getValidationMessages = (id: number, lumpSumEntries: { id: number, amount: string, date: string }[], maxAmount: string, remainingLumpSumAmount: number) => {
  const totalLumpSum = validateLumpSumTotal(lumpSumEntries, remainingLumpSumAmount);
  const messages: { [key: number]: { type: string, message: string } } = {};

  if (totalLumpSum && lumpSumEntries.find(entry => entry.id === id)) {
    messages[id] = { type: 'amount', message: `The total amount exceeds your eligible contribution limit` };
  }
  else {
    const entryId = lumpSumEntries.find(entry => entry.id === id)?.id;
    if (entryId !== undefined) {
      delete messages[entryId];
    }
  }

  return messages;
};

export const convertDate = (date: string) => {
  const [year, month, day] = date.split('/');
  const dateObj = new Date(Number(year), Number(month) - 1, Number(day));
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
  return dateObj.toLocaleDateString('en-US', options);
};

// Calculates adjusted HSA contributions based on previous contribution amount
export const calculateContributionAdjustments = (prevContribution: number, hsaContributionDetails: HSAContributionsResponseDto): ContributionAdjustmentDetails => {
  const matchMethod = hsaContributionDetails.employer_contribution_details.fsa_match_method;
  const remainingEmployeeContribution = Number(hsaContributionDetails.employee_contribution_details.calculated_subscriber_max) - prevContribution;

  switch (matchMethod) {
    case FIXED:
      return remainingEmployeeContribution >= 0 ? adjustEmployeeContribution(remainingEmployeeContribution, hsaContributionDetails) : adjustEmployerContributions(Math.abs(remainingEmployeeContribution), hsaContributionDetails);

    case DONT_CONTRIBUTE:
      return adjustEmployeeContribution(remainingEmployeeContribution, hsaContributionDetails);

    default:
      throw new Error(`Unsupported match method: ${matchMethod}`);
  }
};

// Adjusts employee contributions based on remaining amount
const adjustEmployeeContribution = (remainingAmount: number, details: HSAContributionsResponseDto): ContributionAdjustmentDetails => ({
  employeeContribution: remainingAmount <= 0 ? 0 : remainingAmount,
  employeeAdjusted: true,
  employeeisZero: remainingAmount <= 0,
  employerAdjusted: false,
  employerisZero: false,
  employerTotalFrequencyContribution: Number(details.employer_contribution_details.total_org_contribution_amount),
  employerContributionFrequencyAmount: Number(details.employer_contribution_details.contribution_frequency_amount),
  employerLumpSumAmount: Number(details.employer_contribution_details.total_er_lump_sum_contribution_amount),
  scheduledContributions: details.scheduled_contribution
});


// Adjusts employer contributions based on remaining amount
const adjustEmployerContributions = (remainingAmount: number,  details: HSAContributionsResponseDto): ContributionAdjustmentDetails => {
  const employerDetails = details.employer_contribution_details;
  const employerLumpSum = Number(employerDetails.total_er_lump_sum_contribution_amount);
  const newResult = {
    employeeAdjusted: true,
    employeeisZero: true,
    employeeContribution: 0,
    employerAdjusted: true,
  };
  const newTotalFrequencyContribution = Number(employerDetails.total_org_contribution_amount) - remainingAmount;

  if (newTotalFrequencyContribution >= 0) {
    const pendingEmployerContributions =  Number(employerDetails.total_org_contribution_amount) / Number(employerDetails.contribution_frequency_amount);
    const newFrequencyContributionAmount = Number(newTotalFrequencyContribution.toFixed(2)) / pendingEmployerContributions;
    const newScheduledContributions = adjustScheduledFrequencyContribution(newFrequencyContributionAmount, details.scheduled_contribution);

    return {
      ...newResult,
      employerTotalFrequencyContribution: Number(newTotalFrequencyContribution.toFixed(2)),
      employerContributionFrequencyAmount: Number(newFrequencyContributionAmount.toFixed(2)),
      employerLumpSumAmount: Number(employerLumpSum.toFixed(2)),
      employerisZero: newTotalFrequencyContribution === 0 && employerLumpSum === 0,
      scheduledContributions: newScheduledContributions
    };
  }

  const newLumpSumAmount = employerLumpSum - Math.abs(newTotalFrequencyContribution);
  const newScheduledContributions = adjustScheduledLumpSumContribution(employerLumpSum - newLumpSumAmount, details.scheduled_contribution);

  return {
    ...newResult,
    employerContributionFrequencyAmount: 0,
    employerTotalFrequencyContribution: 0,
    employerisZero: newLumpSumAmount <= 0,
    employerLumpSumAmount: newLumpSumAmount <= 0 ? 0 : Number(newLumpSumAmount.toFixed(2)),
    scheduledContributions: newScheduledContributions
  };
};

const adjustScheduledFrequencyContribution = (FrequencyAmount: number, scheduledContribution: ScheduledContributionDto[]): ScheduledContributionDto[] => {
  return scheduledContribution.map(contribution => ({
    ...contribution,
    scheduled_employer_contribution: parseFloat(contribution.scheduled_employer_contribution) > 0 ? FrequencyAmount.toFixed(2) : contribution.scheduled_employer_contribution
  }));
};

const adjustScheduledLumpSumContribution = (remainingLumpSum: number, scheduledContribution: ScheduledContributionDto[]): ScheduledContributionDto[] => {
  const newscheduledContributions = adjustScheduledFrequencyContribution(0, scheduledContribution);

  return newscheduledContributions.map(contribution => {
    const currentLumpSum = parseFloat(contribution.scheduled_employer_lump_sum_contribution);

    if(currentLumpSum <= 0) {
      return contribution;
    }

    if(remainingLumpSum >= currentLumpSum) {
      remainingLumpSum -= currentLumpSum;
      return{
        ...contribution,
        scheduled_employer_lump_sum_contribution: "0.00"
      };
    }
    else {
      const newLumpSum = currentLumpSum - remainingLumpSum;
      remainingLumpSum = 0;
      return{
        ...contribution,
        scheduled_employer_lump_sum_contribution: newLumpSum.toFixed(2)
      };
    }
  });
};

export const getTotalContributionAmount = (contributions:ScheduledContributionDto[]) => {
  let initialTotalContributionAmount = 0;
  for (let i = 0; i < contributions.length; i++) {
    initialTotalContributionAmount +=
      Number(contributions[i].scheduled_employer_contribution) +
      Number(contributions[i].scheduled_employer_lump_sum_contribution) +
      Number(contributions[i].scheduled_subscriber_contribution) +
      Number(contributions[i].scheduled_subscriber_lump_sum_contribution);
  }
  return initialTotalContributionAmount;
};
export const isUserAllowedToSubmit = (v3HSAData: HSAContributionsResponseDto, showCards: boolean) => {
  if (v3HSAData === undefined) {
    return false;
  }
  return (
    showCards &&
    (v3HSAData?.employee_contribution_details.subscriber_fsa_contribution_type !== "" || Number(v3HSAData?.employee_contribution_details?.total_ee_lump_sum_contribution_amount) > 0 || Number(v3HSAData?.employee_contribution_details?.total_ee_contribution_amount) > 0)
  );
};
